import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useInvites, useRevokeInvite, useResendInvite, useDeleteInvite } from '@/hooks/useInvites';
import { Mail, Calendar, Ban, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';

const statusColors = {
  pending: 'bg-warning/10 text-warning',
  accepted: 'bg-success/10 text-success',
  revoked: 'bg-destructive/10 text-destructive',
  expired: 'bg-muted text-muted-foreground',
};

const roleLabels = {
  admin: 'Administrator',
  csr: 'Customer Service Rep',
  affiliate: 'Affiliate',
};

export function InvitesTable() {
  const { data: invites = [], isLoading } = useInvites();
  const revokeInvite = useRevokeInvite();
  const resendInvite = useResendInvite();
  const deleteInvite = useDeleteInvite();
  const { formatDate } = useTimezoneUtils();
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [inviteToDelete, setInviteToDelete] = useState<{ id: string; email: string } | null>(null);

  const copyInviteLink = (inviteId: string) => {
    toast({
      title: 'Cannot copy link',
      description: 'For security, invite links are only sent via email. Use the resend button to send a new invite email.',
      variant: 'destructive',
    });
  };

  const handleDeleteInvite = () => {
    if (!inviteToDelete) return;
    
    deleteInvite.mutate(inviteToDelete.id, {
      onSuccess: () => {
        setDeleteDialogOpen(false);
        setInviteToDelete(null);
      },
    });
  };

  if (isLoading) {
    return <div className="text-center py-12">Loading invites...</div>;
  }

  if (invites.length === 0) {
    return (
      <>
        <div className="text-center py-12 text-muted-foreground border rounded-lg">
          No pending invitations
        </div>
      </>
    );
  }

  return (
    <>
      <div className="rounded-md border">
        <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Invited By</TableHead>
            <TableHead>Sent</TableHead>
            <TableHead>Expires</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {invites.map((invite) => {
            const isPending = invite.status === 'pending' && new Date(invite.expires_at) > new Date();
            const isExpired = invite.status === 'pending' && new Date(invite.expires_at) <= new Date();
            
            return (
              <TableRow key={invite.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    {invite.email}
                  </div>
                </TableCell>
                <TableCell>
                  {roleLabels[invite.role]}
                </TableCell>
                <TableCell>
                  <Badge className={statusColors[isExpired ? 'expired' : invite.status]}>
                    {isExpired ? 'Expired' : invite.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  {invite.invited_by.full_name}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    {formatDate(new Date(invite.created_at), 'MMM d, yyyy')}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm text-muted-foreground">
                    {formatDate(new Date(invite.expires_at), 'MMM d, yyyy HH:mm')}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    {isPending && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => resendInvite.mutate(invite.id)}
                          disabled={resendInvite.isPending}
                          title="Resend invite email"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => revokeInvite.mutate(invite.id)}
                          disabled={revokeInvite.isPending}
                          title="Revoke invite"
                        >
                          <Ban className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                    {(isExpired || invite.status === 'revoked') && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => resendInvite.mutate(invite.id)}
                        disabled={resendInvite.isPending}
                        title="Resend invite"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Resend
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setInviteToDelete({ id: invite.id, email: invite.email });
                        setDeleteDialogOpen(true);
                      }}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      title="Delete invite permanently"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
    
    <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Invitation</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to permanently delete the invitation for{' '}
            <strong>{inviteToDelete?.email}</strong>? This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDeleteInvite}
            disabled={deleteInvite.isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {deleteInvite.isPending ? 'Deleting...' : 'Delete Invitation'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
